import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { DynamicForm, FormField } from 'src/components/Forms';
import { getDynamicFormDefaultValues } from 'src/components/Forms/DynamicForm/dynamic-form-default-values';
import { ModalContainer } from 'src/components/ModalContainer';
import {
  FollowUpReasons,
  MemberServicesFollowUpInput,
  useMemberServicesFollowUp,
} from 'src/graphql/entities/member-services-followup';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';

export interface MemberServicesFollowUpFormValues {
  reason: { id: string; label: string; value: string };
  additionalNotes: string;
}

const formFields: FormField[] = [
  {
    name: 'reason',
    'data-testid': 'member-services-followup-reason',
    label: 'Reason for follow-up',
    type: 'autocomplete',
    required: true,
    clearable: false,
    options: Object.entries(FollowUpReasons).map(([reasonName, reasonValue]) => ({
      id: reasonName,
      value: reasonValue,
      label: reasonName,
    })),
  },
  {
    id: 'additionalNotes',
    name: 'additionalNotes',
    'data-testid': 'member-services-followup-notes',
    label: 'Additional Notes',
    type: 'textfield',
    textFieldProps: {
      InputProps: {
        multiline: true,
        minRows: 4,
      },
    },
  },
];

export const MemberServicesFollowUpModal = () => {
  const { closeModal } = useModalMethods(Modals.MemberServicesFollowUp);
  const { isOpened, modalProps } = useModalState(Modals.MemberServicesFollowUp);
  const { postMemberServicesFollowUp } = useMemberServicesFollowUp();

  const formMethods = useForm<MemberServicesFollowUpFormValues>({
    defaultValues: getDynamicFormDefaultValues<MemberServicesFollowUpFormValues>(formFields),
    mode: 'onChange',
  });
  const { formState, handleSubmit } = formMethods;

  const onSubmit = useCallback(
    async (data: MemberServicesFollowUpFormValues) => {
      if (!modalProps?.addressId) return;

      await postMemberServicesFollowUp({
        ...data,
        addressId: modalProps.addressId,
        conversationId: modalProps.conversationId,
        additionalNotes: data.additionalNotes,
        reason: data.reason.value as unknown as MemberServicesFollowUpInput['reason'],
      });
      closeModal();
    },
    [
      closeModal,
      modalProps?.addressId,
      modalProps?.conversationId,
      postMemberServicesFollowUp,
    ],
  );

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Member Services Follow-up"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'member-services-followup-cancel' },
        {
          label: 'Send follow-up',
          kind: 'primary',
          onClick: handleSubmit(onSubmit),
          disabled: !formState.isValid,
          loading: formState.isSubmitting,
          testId: 'member-services-followup-submit',
        },
      ]}
      onClose={closeModal}
      data-testid="member-services-followup-modal"
    >
      <DynamicForm fields={formFields} formMethods={formMethods} sx={{ gap: 2 }} />
    </ModalContainer>
  );
};
